const API_BASE = 'http://127.0.0.1:8093'
export const ADMIN_TOKEN_KEY = 'live_admin_token'

let adminAuthRedirecting = false
let adminAuthHandler = null
let adminLoginGraceUntil = 0

export function buildApiUrl(path) {
  const normalized = path.startsWith('/') ? path : `/${path}`
  // 浏览器内使用相对路径，与页面同源（dev 经 vue proxy 转发 /api）
  if (typeof window !== 'undefined') {
    return normalized
  }
  return `${API_BASE}${normalized}`
}

export function getAdminToken() {
  return localStorage.getItem(ADMIN_TOKEN_KEY)
}

/** 公开接口可选附带管理员 Bearer（首页昵称/点赞/评论） */
export function buildOptionalAuthHeaders(extraHeaders = {}) {
  const headers = {
    'Content-Type': 'application/json',
    ...extraHeaders
  }
  const token = getAdminToken()
  if (token) {
    headers.Authorization = `Bearer ${token}`
  }
  return headers
}

export function isReservedNickname(name) {
  const trimmed = String(name || '').trim()
  if (!trimmed) {
    return false
  }
  if (trimmed === '小葱') {
    return true
  }
  return trimmed.toLowerCase() === 'monster'
}

export function setAdminToken(token) {
  if (token) {
    localStorage.setItem(ADMIN_TOKEN_KEY, token)
  } else {
    localStorage.removeItem(ADMIN_TOKEN_KEY)
  }
}

export function clearAdminSession() {
  setAdminToken(null)
  adminLoginGraceUntil = 0
}

/** 登录成功后短暂忽略陈旧请求的 401，避免被误踢出 */
export function markAdminLoginGrace(ms = 4000) {
  adminLoginGraceUntil = Date.now() + ms
}

function isAdminLoginGraceActive() {
  return Date.now() < adminLoginGraceUntil
}

/** 由 main.js 注入，token 失效时跳转登录页 */
export function setAdminAuthHandler(handler) {
  adminAuthHandler = typeof handler === 'function' ? handler : null
}

/**
 * @returns {Promise<boolean|null>} true=有效, false=已失效, null=暂无法校验(如 503)不误判登出
 */
export async function verifyAdminSession() {
  const token = getAdminToken()
  if (!token) {
    return false
  }
  try {
    const response = await fetch(buildApiUrl('/api/admin/auth/session'), {
      headers: { Authorization: `Bearer ${token}` }
    })
    if (response.status === 503) {
      return null
    }
    if (!response.ok) {
      return false
    }
    const result = await response.json()
    return Boolean(result?.success)
  } catch {
    return null
  }
}

/** 仅当会话 API 确认失效时才跳转登录页；返回 true 表示已踢出 */
export async function confirmAdminSessionInvalid() {
  const valid = await verifyAdminSession()
  if (valid === false) {
    handleAdminUnauthorized()
    return true
  }
  return false
}

export function handleAdminUnauthorized() {
  if (typeof window === 'undefined') {
    return
  }
  if (isAdminLoginGraceActive()) {
    return
  }
  if (adminAuthRedirecting) {
    return
  }
  const path = window.location.pathname || ''
  const onLoginPage = path === '/set' || path.endsWith('/set')
  clearAdminSession()
  if (onLoginPage) {
    return
  }
  adminAuthRedirecting = true
  if (adminAuthHandler) {
    adminAuthHandler()
  } else {
    window.location.assign('/set?expired=1')
  }
  window.setTimeout(() => {
    adminAuthRedirecting = false
  }, 1500)
}

export async function adminFetch(path, options = {}) {
  const token = getAdminToken()
  const headers = {
    'Content-Type': 'application/json',
    ...(options.headers || {})
  }
  if (token) {
    headers.Authorization = `Bearer ${token}`
  }
  const response = await fetch(buildApiUrl(path), {
    ...options,
    headers
  })
  let result = null
  try {
    result = await response.json()
  } catch (error) {
    result = { success: false, message: '响应解析失败' }
  }
  if (response.status === 401) {
    if (!isAdminLoginGraceActive()) {
      handleAdminUnauthorized()
    }
    return { response, result, unauthorized: true, serviceUnavailable: false }
  }
  if (response.status === 503) {
    return { response, result, unauthorized: false, serviceUnavailable: true }
  }
  return { response, result, unauthorized: false, serviceUnavailable: false }
}
